/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { GenerativeCommandsEnum } from '@squiz/dxp-ai-types';
import {
  Alert,
  ALERT_TYPE_REPORT,
  Card,
  Grid,
  GridItem,
  Spinner,
  TextField,
  TEXTFIELD_TYPE_TEXTAREA,
  TextFieldProps,
} from '@squiz/sds';
import React, { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON_PLAY_CIRCLE_OUTLINE } from '../../../../../../libs/sds/src/Icons';
import { TEST_TOOL_REFRESH_RATE } from '../../../Constants/ConfigurationVariables.constants';
import { AI_FORM_IDS, AI_STRINGS } from '../../../Constants/Strings.constants';
import { getAiService } from '../../../Services/ai';
import { PreviewButton } from '../../PreviewButton/PreviewButton';
import { TEST_TOOL_DATA_IDS, TEST_TOOL_FORM_LABELS, TEST_TOOL_INPUT_LIMITS } from '../TestTool.constants';
import { TestToolProps } from '../TestTool.props';

export const GenerativeTest = ({ contentTool }: TestToolProps): JSX.Element => {
  const contentToolTestInputFieldProps: TextFieldProps = {
    classes: 'content-tool-test-input',
    errorMessage: null,
    fieldLabel: '',
    id: AI_FORM_IDS.testInput,
    name: AI_FORM_IDS.testInput,
    placeholder: AI_STRINGS.testToolInputPlaceHolder,
    rows: 17,
    type: TEXTFIELD_TYPE_TEXTAREA,
  };
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inputLimitExceeded, setInputLimitExceeded] = useState(false);
  const [inputValidationMessage, setInputValidationMessage] = useState('');
  const [inputValidationMessageClass, setInputValidationMessageClass] = useState(
    'content-tool-test-input-validation-message',
  );
  const [outputContent, setOutputContent] = useState(AI_STRINGS.testToolOutputPlaceHolder);
  const contentToolInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const aiService = getAiService();
  const { maxMessageLength, showNumCharactersTypedLength } = TEST_TOOL_INPUT_LIMITS;

  const onClick = async (): Promise<void> => {
    setErrorMessage('');
    setLoading(true);
    const updateOutput = (flowExecutionId: string): void => {
      setLoading(true);
      const interval = setInterval(async () => {
        try {
          const res = await aiService.apiClient.generative.getExecutionOutput(
            contentTool.id,
            GenerativeCommandsEnum.processMessage,
            flowExecutionId,
          );

          if (res.status === 'pending' || res.status === 'executing') {
            return;
          }
          if (res.status === 'failed') {
            throw new Error(AI_STRINGS.executionFailed);
          }
          if (res.status === 'cancelled') {
            throw new Error(AI_STRINGS.executionCancelled);
          }
          if (res.status === 'succeeded') {
            clearInterval(interval);
            const response = res.outputs.Message.Response;
            setOutputContent(response);
            setErrorMessage('');
            setLoading(false);
          }
        } catch (e) {
          clearInterval(interval);
          if (e instanceof Error) {
            setErrorMessage(e.message);
            setLoading(false);
          }
        }
      }, TEST_TOOL_REFRESH_RATE);
    };

    const inputValue = contentToolInputRef.current && contentToolInputRef.current.value;
    try {
      if (!inputValue || inputValue === '') {
        throw new Error(AI_STRINGS.inputEmpty);
      }

      setInputValidationMessage('');
      setInputLimitExceeded(false);
      setLoading(true);

      const res = await aiService.apiClient.generative.execute(contentTool.id, GenerativeCommandsEnum.processMessage, {
        inputs: {
          IsPreview: true,
          Query: inputValue,
        },
      });
      updateOutput(res.executionId);
    } catch (e) {
      if (e instanceof Error) {
        setErrorMessage(e.message);
      }
      setLoading(false);
    }
  };

  const handleOnChange = (_: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = contentToolInputRef.current && contentToolInputRef.current.value;
    if (!inputValue || inputValue === '') {
      setInputValidationMessage('');
      setInputLimitExceeded(false);
      return;
    } else if (inputValue.length > showNumCharactersTypedLength && inputValue.length < maxMessageLength) {
      setInputValidationMessage(`${inputValue.length}/${maxMessageLength}`);
      setInputValidationMessageClass('content-tool-test-input-validation-message');
      setInputLimitExceeded(false);
    } else if (inputValue.length >= maxMessageLength) {
      setInputValidationMessage(`${inputValue.length}/${maxMessageLength} limit reached`);
      setInputValidationMessageClass('content-tool-test-input-validation-message__invalid');
      setInputLimitExceeded(inputValue.length > maxMessageLength);
      return;
    } else {
      setInputValidationMessage('');
      setInputLimitExceeded(false);
    }
  };

  const inputComponent = (
    <div className="content-tool-test-container">
      <div className="content-tool-test-heading">
        <span className="content-tool-test-heading-text">Input</span>
        {inputValidationMessage !== '' && (
          <div className={inputValidationMessageClass} data-testid={TEST_TOOL_DATA_IDS.testToolValidationMessage}>
            {inputValidationMessage}
          </div>
        )}
        <PreviewButton
          dataTestId={'test-tool-run-button'}
          disabled={inputLimitExceeded}
          icon={ICON_PLAY_CIRCLE_OUTLINE}
          loading={loading}
          name={t(AI_STRINGS.run)}
          onClick={onClick}
        />
      </div>
      <TextField
        data-id={TEST_TOOL_DATA_IDS.testToolInputField}
        {...contentToolTestInputFieldProps}
        classes="content-tool-test-input"
        onChange={handleOnChange}
        ref={contentToolInputRef}
      />
    </div>
  );

  const outputComponent = (
    <div className="content-tool-test-container">
      <div className="content-tool-test-heading">
        <span className="content-tool-test-heading-text">Output</span>
      </div>
      {errorMessage ? (
        <Alert
          aria-label={TEST_TOOL_FORM_LABELS.testToolAlert}
          hasBgColor
          message={errorMessage}
          type={ALERT_TYPE_REPORT}
        />
      ) : loading ? (
        <div
          aria-label={TEST_TOOL_FORM_LABELS.testToolLoadingSpinner}
          className="sds-card-container content-tool-test-card content-tool-loading"
        >
          <Spinner />
        </div>
      ) : (
        <Card
          aria-label={TEST_TOOL_FORM_LABELS.testToolOutputField}
          classes="content-tool-test-card"
          content={outputContent}
          format="standard"
          title=""
        />
      )}
    </div>
  );

  const gridItemInput: GridItem = {
    component: inputComponent,
    componentName: 'content-tool-input',
    componentSize: {
      colWidth: 6,
      rowHeight: 0,
    },
    key: 'content-tool-input',
  };

  const gridItemOutput: GridItem = {
    component: outputComponent,
    componentName: 'content-tool-output',
    componentSize: {
      colWidth: 6,
      rowHeight: 0,
    },
    key: 'content-tool-output',
  };

  return <Grid items={[gridItemInput, gridItemOutput]} />;
};
